import { Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { formatCurrency } from "web_myplann/Infrastructure/Utils/CurrencyUtils";
import { AffordabilityRow } from "web_myplann/Inspiration/Components/AffordabilityRow";
import { InspirationSection } from "web_myplann/Inspiration/Components/InspirationSection";
import { INSPIRATION_CURRENCY } from "web_myplann/Inspiration/constants";
import type { EarlyFreedomContext } from "web_myplann/Inspiration/Utils/earlyFreedomMath";

const R = Resources.Inspiration.EarlyFreedom;

type Props = {
	context: EarlyFreedomContext;
};

export const EarlyFreedomCapitalSection: FC<Props> = ({ context }) => {
	const { t } = useResource();
	const theme = useTheme();

	const divider = theme.colors.border.solid(theme.palette.grey[200]);

	return (
		<InspirationSection title={t(R.capitalTitle)} gap={1.2}>
			<Stack
				bgcolor={theme.palette.surface.muted}
				borderRadius={theme.radii.lg}
				px={1.6}
				py={1.2}
			>
				<Stack gap={0.6}>
					<AffordabilityRow
						label={t(R.rowExpenses)}
						value={formatCurrency(
							context.targetExpenses,
							INSPIRATION_CURRENCY,
							0,
						)}
					/>
					<AffordabilityRow
						label={t(R.rowPassive)}
						value={`− ${formatCurrency(context.passiveToday, INSPIRATION_CURRENCY, 0)}`}
						valueColor={theme.palette.green.main}
					/>
				</Stack>

				<Stack
					direction="row"
					alignItems="baseline"
					justifyContent="space-between"
					mt={1.2}
					pt={1.2}
					borderTop={divider}
				>
					<Typography variant="bodyMedium">{t(R.rowGap)}</Typography>
					<Typography variant="subtitle3" tabular>
						{formatCurrency(context.gap, INSPIRATION_CURRENCY, 0)}
						<Typography
							component="span"
							fontSize="12px"
							fontWeight={500}
							sx={{ opacity: 0.7 }}
						>
							{t(R.perMonth)}
						</Typography>
					</Typography>
				</Stack>

				<Stack mt={1.6} pt={1.6} borderTop={divider}>
					<Typography variant="button" color={theme.palette.grey[500]}>
						{t(R.capitalLabel)}
					</Typography>
					<Typography
						variant="h1"
						color={theme.palette.orange.main}
						tabular
						mt={0.4}
					>
						~{formatCurrency(context.targetCapital, INSPIRATION_CURRENCY, 0)}
					</Typography>
					<Typography variant="body1" color={theme.palette.grey[500]} mt={0.2}>
						{t(R.capitalNote)}
					</Typography>
				</Stack>
			</Stack>

			<Typography
				variant="hint"
				color={theme.palette.grey[400]}
				lineHeight={1.625}
			>
				{t(R.capitalDisclaimer)}
			</Typography>
		</InspirationSection>
	);
};
